/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Key List Table Component (Task 10.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Key,
  Plus,
  Search,
  Copy,
  Check,
  Ban,
  Clock,
  Zap,
  Power,
  ShieldAlert,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useApiKeyStore,
  ApiKeyItem,
} from '../../stores/useApiKeyStore';

export const ApiKeyListTable: React.FC = () => {
  const { t } = useTranslation();
  const {
    searchQuery,
    statusFilter,
    setSearchQuery,
    setStatusFilter,
    getFilteredKeys,
    setCreateModalOpen,
    setRevokeModalOpen,
    setKeyToRevoke,
    toggleKeyActive,
  } = useApiKeyStore();

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredKeys = getFilteredKeys();

  const handleCopyHint = async (key: ApiKeyItem) => {
    try {
      await navigator.clipboard.writeText(`${key.keyPrefix}••••••••${key.keyHint}`);
      setCopiedId(key.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
    }
  };

  const handleOpenRevoke = (key: ApiKeyItem) => {
    setKeyToRevoke(key);
    setRevokeModalOpen(true);
  };

  const formatRelativeTime = (timestamp: number | null): string => {
    if (!timestamp) return t('apikeys.neverUsed');
    const diffMs = Date.now() - timestamp;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 5) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
      {/* Top Filter & Action Bar */}
      <div className="p-5 border-b border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="size-4 text-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('apikeys.searchPlaceholder')}
            className="w-full text-xs rounded-xl border border-border bg-surface-subtle pl-10 pr-3 py-2.5 text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* Filter Tabs & New Key Button */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="p-1 rounded-xl bg-surface-subtle border border-border flex items-center gap-1">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === 'all'
                  ? 'bg-surface text-text shadow-xs'
                  : 'text-text-muted hover:text-text'
              }`}
            >
              {t('apikeys.filterAll')}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === 'active'
                  ? 'bg-surface text-text shadow-xs'
                  : 'text-text-muted hover:text-text'
              }`}
            >
              {t('apikeys.filterActive')}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('revoked')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === 'revoked'
                  ? 'bg-surface text-text shadow-xs'
                  : 'text-text-muted hover:text-text'
              }`}
            >
              {t('apikeys.filterRevoked')}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="px-4 py-2.5 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm flex items-center gap-2"
          >
            <Plus className="size-4" />
            <span>{t('apikeys.createNewKey')}</span>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-border bg-surface-subtle/50 text-text-muted font-semibold">
              <th className="py-3.5 px-5">{t('apikeys.colName')}</th>
              <th className="py-3.5 px-5">{t('apikeys.colKey')}</th>
              <th className="py-3.5 px-5">{t('apikeys.colScopes')}</th>
              <th className="py-3.5 px-5">{t('apikeys.colRateLimit')}</th>
              <th className="py-3.5 px-5">{t('apikeys.colStatus')}</th>
              <th className="py-3.5 px-5">{t('apikeys.colLastUsed')}</th>
              <th className="py-3.5 px-5 text-right">{t('apikeys.colActions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredKeys.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12 px-4 text-text-muted">
                  <div className="p-3 rounded-full bg-surface-subtle text-text-muted inline-block mb-2">
                    <Key className="size-6" />
                  </div>
                  <h4 className="text-sm font-semibold text-text mb-1">
                    {t('apikeys.emptyTitle')}
                  </h4>
                  <p className="text-xs max-w-sm mx-auto">
                    {t('apikeys.emptyDesc')}
                  </p>
                </td>
              </tr>
            ) : (
              filteredKeys.map((key) => {
                const isTest = key.keyPrefix.includes('test');
                return (
                  <tr
                    key={key.id}
                    className={`hover:bg-surface-subtle/40 transition-colors ${
                      !key.isActive ? 'opacity-65' : ''
                    }`}
                  >
                    {/* 1. Name & Environment */}
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`p-1.5 rounded-lg border ${
                            isTest
                              ? 'bg-amber-500/10 border-amber-500/20 text-amber-500'
                              : 'bg-primary/10 border-primary/20 text-primary'
                          }`}
                        >
                          <Key className="size-4" />
                        </span>
                        <div>
                          <div className="font-semibold text-text text-sm flex items-center gap-1.5">
                            <span>{key.name}</span>
                            <span
                              className={`text-[10px] font-mono px-1.5 py-0.2 rounded font-bold uppercase ${
                                isTest
                                  ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                                  : 'bg-primary/10 text-primary border border-primary/20'
                              }`}
                            >
                              {isTest ? 'TEST' : 'LIVE'}
                            </span>
                          </div>
                          <div className="text-[11px] text-text-muted font-mono">
                            {key.id}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 2. Key Prefix & Hint */}
                    <td className="py-4 px-5">
                      <div className="inline-flex items-center gap-1.5 bg-surface-subtle border border-border px-2.5 py-1 rounded-lg font-mono text-[11px]">
                        <span className="text-text-muted">{key.keyPrefix}</span>
                        <span className="text-text-muted">••••••••</span>
                        <span className="font-bold text-text">{key.keyHint}</span>
                        <button
                          type="button"
                          onClick={() => handleCopyHint(key)}
                          className="p-1 rounded hover:bg-surface text-text-muted hover:text-text transition-colors ml-1"
                          title="Copy key hint"
                        >
                          {copiedId === key.id ? (
                            <Check className="size-3 text-emerald-500" />
                          ) : (
                            <Copy className="size-3" />
                          )}
                        </button>
                      </div>
                    </td>

                    {/* 3. Scopes */}
                    <td className="py-4 px-5">
                      <div className="flex flex-wrap items-center gap-1 max-w-xs">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium border ${
                              scope === '*'
                                ? 'bg-rose-500/10 text-rose-500 border-rose-500/20 font-bold'
                                : 'bg-surface-subtle text-text border-border'
                            }`}
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* 4. Rate Limit RPM */}
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-1.5 font-mono text-xs text-text">
                        <Zap className="size-3.5 text-amber-500" />
                        <span>{key.rateLimitRpm} RPM</span>
                      </div>
                    </td>

                    {/* 5. Status Badge */}
                    <td className="py-4 px-5">
                      {key.isActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          {t('apikeys.statusActive')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-surface-subtle text-text-muted border border-border">
                          <Ban className="size-3" />
                          {t('apikeys.statusRevoked')}
                        </span>
                      )}
                    </td>

                    {/* 6. Last Used At */}
                    <td className="py-4 px-5 text-text-muted font-mono text-[11px]">
                      <div className="flex items-center gap-1">
                        <Clock className="size-3" />
                        <span>{formatRelativeTime(key.lastUsedAt)}</span>
                      </div>
                    </td>

                    {/* 7. Actions */}
                    <td className="py-4 px-5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleKeyActive(key.id)}
                          className={`p-1.5 rounded-lg border transition-colors ${
                            key.isActive
                              ? 'border-border hover:bg-surface-subtle text-text-muted hover:text-text'
                              : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
                          }`}
                          title={key.isActive ? 'Deactivate Key' : 'Activate Key'}
                        >
                          <Power className="size-3.5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenRevoke(key)}
                          className="p-1.5 rounded-lg border border-border hover:bg-rose-500/10 text-rose-500 transition-colors"
                          title="Revoke Key"
                        >
                          <ShieldAlert className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
